import React, { useState, useEffect } from "react";
import { Check, ArrowLeft, Loader2, User, Mail, Phone, KeyRound, Languages } from "lucide-react";
import { register, getLanguages } from "../lib/api";
import { useAudio } from "./AudioProvider";
import { toast } from "sonner";

export default function Register({ onRegistered, onBack }) {
  const { speak } = useAudio();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [language, setLanguage] = useState("gu");
  const [languages, setLanguages] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getLanguages().then(setLanguages).catch(() => {});
    const t = setTimeout(() => speak("નવું ખાતું બનાવો. તમારું નામ, ઈમેલ અને ગુપ્ત નંબર લખો.", 1.0, "reg-help"), 400);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  const submit = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Name and email required / નામ અને ઈમેલ જરૂરી"); return; }
    if (!/^\d{3,}$/.test(pin)) { toast.error("PIN must be at least 3 digits / ઓછામાં ઓછા ૩ આંકડા"); return; }
    setBusy(true);
    try {
      const res = await register({ name, email, phone, pin, language });
      if (res.ok) {
        toast.success("Account created / ખાતું બન્યું");
        onRegistered(res);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not register");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-[#FAFAF7]" data-testid="register-screen">
      <div className="w-full max-w-sm">
        <button onClick={onBack} data-testid="register-back" className="yb-tactile bg-white px-4 py-2 font-bold flex items-center gap-2 mb-4">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-[#FFD600] border-3 border-[#1A1C19] flex items-center justify-center" style={{ boxShadow: "0 4px 0 0 #1A1C19" }}>
            <span className="text-xl font-extrabold">યુ</span>
          </div>
          <div>
            <h1 className="yb-heading text-2xl font-extrabold">New account</h1>
            <p className="text-sm text-[#50564F]">નવું ખાતું બનાવો</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="relative">
            <User className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[#50564F]" />
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name / નામ" data-testid="register-name"
              className="w-full yb-tactile bg-white pl-11 pr-4 py-3 text-lg outline-none" />
          </div>
          <div className="relative">
            <Mail className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[#50564F]" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email / ઈમેલ" data-testid="register-email"
              className="w-full yb-tactile bg-white pl-11 pr-4 py-3 text-lg outline-none" />
          </div>
          <div className="relative">
            <Phone className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[#50564F]" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional) / ફોન (વૈકલ્પિક)" data-testid="register-phone"
              className="w-full yb-tactile bg-white pl-11 pr-4 py-3 text-lg outline-none" />
          </div>
          <div className="relative">
            <KeyRound className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[#50564F]" />
            <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="Numerical PIN / ગુપ્ત નંબર" data-testid="register-pin"
              className="w-full yb-tactile bg-white pl-11 pr-4 py-3 text-2xl font-extrabold tracking-widest outline-none" />
          </div>
          <div className="relative">
            <Languages className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[#50564F] z-10" />
            <select value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="register-language"
              className="w-full yb-tactile bg-white pl-11 pr-4 py-3 text-lg font-bold outline-none">
              {languages.map((l) => <option key={l.code} value={l.code}>{l.name} ({l.english})</option>)}
            </select>
          </div>

          <button onClick={submit} disabled={busy} data-testid="register-submit"
            className="yb-tactile w-full py-4 bg-[#15803D] text-white text-xl font-extrabold flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <Check className="w-7 h-7" strokeWidth={3} />} Create account
          </button>
        </div>
      </div>
    </div>
  );
}
